import axios from "axios";
import { BlockBlobClient } from "@azure/storage-blob";

const normalizeUploadUrl = (rawUrl) => {
  if (typeof rawUrl !== "string") {
    throw new Error("Invalid upload URL: expected a string");
  }

  let url = rawUrl.trim();

  // Some backend responses wrap URL in quotes/parentheses, e.g. "(https://...)"
  if (url.startsWith("(")) url = url.slice(1);
  if (url.endsWith(")")) url = url.slice(0, -1);
  if (
    (url.startsWith('"') && url.endsWith('"')) ||
    (url.startsWith("'") && url.endsWith("'"))
  ) {
    url = url.slice(1, -1);
  }

  try {
    return new URL(url).toString();
  } catch {
    throw new Error(`Invalid upload URL after normalization: ${url}`);
  }
};

/**
 * Step 1: Request SAS URL from backend
 */
export const requestUpload = async (payload) => {
  try {
    const response = await axios.post(
      "/workbench/ApplicantRequestUpload",
      payload,
    );

    return response.data; // { uploadUrl, documentId }
  } catch (error) {
    console.error("RequestUpload failed:", error);
    throw error;
  }
};

/**
 * Step 2: Upload file directly to Azure Blob
 */
export const uploadFileToBlob = async (file, uploadUrl, onProgress) => {
  try {
    const normalizedUploadUrl = normalizeUploadUrl(uploadUrl);
    const blockBlobClient = new BlockBlobClient(normalizedUploadUrl);
    await blockBlobClient.uploadData(file, {
      blockSize: 4 * 1024 * 1024, // 4MB
      concurrency: 5,
      onProgress: (ev) => {
        if (onProgress) {
          const percent = Math.round((ev.loadedBytes / file.size) * 100);
          onProgress(percent);
        }
      },
    });
    return true;
  } catch (error) {
    console.error("Azure upload failed:", error);
  }
};

/**
 * Step 3: Confirm upload with backend
 */
export const confirmUpload = async (documentId) => {
  try {
    await axios.post("/workbench/ApplicantConfirmUpload", {
      documentId,
    });

    return true;
  } catch (error) {
    console.error("ConfirmUpload failed:", error);
    throw error;
  }
};

/**
 * Full upload workflow (optional wrapper)
 */
export const uploadDocument = async (file, metadataPayload, onProgress) => {
  // 1. Request SAS
  const { uploadUrl, documentId } = await requestUpload({
    ...metadataPayload,
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type,
  });

  // 2. Upload to Azure
  await uploadFileToBlob(file, uploadUrl, onProgress);

  // 3. Confirm
  await confirmUpload(documentId);

  return documentId;
};
